import dayjs from "dayjs"
import { ANNUAL_PRICE, MONTHLY_PRICE, DASHER_TOKEN, TRELLO_TOKEN_KEY_MAP } from "./conifg"
import { Trello } from "./trello"
import { ImportQuota, Subscription } from "./types"

export const getPlanMessage = (
  currentSubscription: Partial<Subscription>,
  pupDisplayName: string,
  /**
   * Mention the monthly plan in the call to action.
   *
   * Opt-in for the same reason as DasherPremium's `monthlyPlan`: the other
   * power-ups share this copy and still offer annual only.
   */
  options?: {
    showMonthly?: boolean
    /**
     * Free-tier state for a power-up metered in uses rather than in days.
     *
     * The workspace-wide trial row still exists for these power-ups — it is
     * created on first login to any of them — so without this the page reads
     * the trial's status and announces an expiry that has nothing to do with
     * what the user is being asked to buy.
     */
    freeImports?: ImportQuota
  }
) => {
  // One subscription covers the whole workspace, whichever plan pays for it.
  const cta = options?.showMonthly
    ? `A $${ANNUAL_PRICE}/year or $${MONTHLY_PRICE}/month subscription unlocks all Blue Dasher Power-Ups for everyone in your workspace`
    : `A $${ANNUAL_PRICE}/year subscription unlocks all Blue Dasher Power-Ups for everyone in your workspace`

  const quota = options?.freeImports
  const freeImportsStatus = quota
    ? quota.remaining > 0
      ? `You have ${quota.remaining} of ${quota.limit} free imports left. Subscribe to Blue Dasher for unlimited imports with ${pupDisplayName}`
      : `Your workspace has used all ${quota.limit} free imports. Purchase a Blue Dasher subscription to continue using ${pupDisplayName}`
    : null

  switch (currentSubscription.status) {
    case "active":
      return {
        status: `Your Blue Dasher subscription will renew for $${
          currentSubscription.unitPrice
        } on ${dayjs(currentSubscription.nextBillDate).format("MMM DD, YYYY")}`,
        cta: `Your workspace currently has access to all Blue Dasher Power-Ups`
      }
    case "trialing":
      if (freeImportsStatus) {
        return { status: freeImportsStatus, cta }
      }
      if (
        dayjs(currentSubscription.cancellationEffectiveDate).diff(
          dayjs(),
          "day"
        ) > 0
      ) {
        return {
          status: `Your trial will expire on ${dayjs(
            currentSubscription.cancellationEffectiveDate
          ).format(
            "MMM DD, YYYY"
          )}. Purchase a Blue Dasher subscription to continue using ${pupDisplayName}`,
          cta
        }
      } else {
        return {
          status: `Your trial has expired. Purchase a Blue Dasher subscription to continue using ${pupDisplayName}`,
          cta
        }
      }
    case "past_due":
      return {
        status: "Your subscription is past due",
        cta:
          quota && quota.remaining > 0
            ? `Update your payment method to restore unlimited use. Your workspace has ${quota.remaining} free imports left in the meantime`
            : `Update your payment method to maintain access to Blue Dasher Power-Ups`
      }
    case "deleted":
      if (
        dayjs(currentSubscription.cancellationEffectiveDate).diff(
          dayjs(),
          "day"
        ) > 0
      ) {
        return {
          status: `Your subscription to Blue Dasher will expire on ${dayjs(
            currentSubscription.cancellationEffectiveDate
          ).format("MMM DD, YYYY")}`,
          cta
        }
      } else {
        if (quota) {
          // Free imports outlive the subscription that used to cover them, so
          // a lapsed workspace with credits left still has a way in.
          return {
            status:
              quota.remaining > 0
                ? `Your subscription has expired. ${quota.remaining} of ${quota.limit} free imports remain for ${pupDisplayName}`
                : `Your subscription has expired. Subscribe to Blue Dasher to continue using ${pupDisplayName}`,
            cta
          }
        }
        return {
          status: `Your subscription or trial has expired. Subscribe to Blue Dasher to continue using ${pupDisplayName}`,
          cta
        }
      }

    default:
      return {
        status: freeImportsStatus ?? "Your subscription or trial has expired",
        cta,
      }
  }
}

/** Why an authorization attempt was refused, in words the user can act on. */
export interface AuthorizationFailure {
  message: string
  /** False when the refusal is a dead end — clicking Authorize again cannot fix it. */
  canRetry: boolean
}

/**
 * Turn a failed token exchange into something worth showing.
 *
 * `POST /v1/token` refuses a user who holds a valid Trello token but isn't
 * entitled to the power-up — most often a board guest, who can see the board
 * but isn't a member of the workspace. The server writes those refusals to be
 * acted on (naming the workspace, saying who to ask), and this is what gets
 * them onto the screen: without it the rejection was thrown away and the button
 * simply stopped responding, so people clicked it again and again.
 */
export const authorizationFailure = (error: unknown): AuthorizationFailure => {
  const response = (error as {
    response?: { status?: number; data?: { detail?: string; code?: string } }
  })?.response

  if (!response) {
    return {
      message:
        "Couldn't reach Blue Dasher to finish authorizing. Check your connection and try again.",
      canRetry: true,
    }
  }

  const detail = response.data?.detail
  const code = response.data?.code

  // Entitlement refusals. Retrying is pointless — the fix is in Trello.
  if (code === "not_workspace_member" || code === "no_board_access") {
    return { message: detail ?? "You don't have access to this Power-Up.", canRetry: false }
  }

  if (response.status === 403) {
    return {
      message:
        detail ??
        "You don't have permission to use this Power-Up here. Ask a workspace admin to add you.",
      canRetry: false,
    }
  }

  // A rejected Trello token: authorizing again is exactly the right move.
  if (response.status === 400) {
    return {
      message: detail ?? "Trello didn't accept that authorization. Please try again.",
      canRetry: true,
    }
  }

  return {
    message: detail ?? "Something went wrong while authorizing. Please try again.",
    canRetry: true,
  }
}

export function getTrelloToken(t: Trello.PowerUp.IFrame, pupId: string) {
  return t.get("member", "private", TRELLO_TOKEN_KEY_MAP[pupId])
}

export function setTrelloToken(
  t: Trello.PowerUp.IFrame,
  token: string,
  pupId: string
) {
  return t.set("member", "private", TRELLO_TOKEN_KEY_MAP[pupId], token)
}

export function clearTrelloToken(t: Trello.PowerUp.IFrame, pupId: string) {
  return t.remove("member", "private", TRELLO_TOKEN_KEY_MAP[pupId])
}

export function getDasherToken(t: Trello.PowerUp.IFrame) {
  return t.get("member", "private", DASHER_TOKEN)
}

export function setDasherToken(t: Trello.PowerUp.IFrame, token: string) {
  return t.set("member", "private", DASHER_TOKEN, token)
}

export function clearDasherToken(t: Trello.PowerUp.IFrame) {
  return t.remove("member", "private", DASHER_TOKEN)
}

export const validateTrelloToken = (token: unknown): boolean => {
  if (typeof token !== "string" || !token || token.includes("error")) {
    return false
  } else {
    return true
  }
}
